<template>
  <div>
    <h1 class="p">
      {{ $t("events") }}
    </h1>

    <ProgressBar v-if="isLoading" mode="indeterminate" class="mb-3" />

    <p v-else-if="!events.length" class="p text-center text-color-secondary">
      {{ $t("no_events") }}
    </p>

    <div v-else class="grid">
      <div
        v-for="event in events"
        :key="event.id"
        class="col-12 sm:col-6 lg:col-4 xl:col-3 p-2"
        :class="isClickable(event) ? 'cursor-pointer' : null"
        @click="openEvent(event)"
      >
        <div class="p-4 border-1 surface-border border-round h-full">
          <div class="mb-3 align-items-center">
            <ShowUserImage
              v-if="event.imageUrl"
              :url="event.imageUrl"
              class="no_rounded mr-0 responsive-bg-16by9"
            />
          </div>
          <div v-if="isConference(event)" class="mb-2">
            <span class="event-type-tag event-type-tag--conference">
              {{ $t("conference") }}
            </span>
          </div>
          <div class="mb-2 font-bold">
            {{ formatSchedule(event) }}
          </div>
          <div class="mb-4 font-semibold">{{ event.title }}</div>
          <div v-if="event.location || event.room" class="mb-2">
            <i class="pi pi-map-marker mr-1" />
            {{ [event.location, event.room].filter(Boolean).join(" · ") }}
          </div>
          <div v-if="event.authors" class="mb-2">
            Autor(es): {{ event.authors }}
          </div>
          <div>{{ event.description }}</div>
          <div
            v-if="isConference(event) && formatPrice(event)"
            class="mt-3 font-semibold"
          >
            {{ formatPrice(event) }}
            <span
              v-if="event.pricePhaseName"
              class="font-normal text-color-secondary"
            >
              · {{ event.pricePhaseName }}
            </span>
          </div>
          <div v-if="isConference(event)" class="mt-3" @click.stop>
            <Button
              v-if="event.isRegistered"
              :label="$t('conference_registered_label')"
              class="p-button-sm p-button-outlined w-full justify-content-center"
              disabled
            />
            <Button
              v-else-if="event.soldOut"
              :label="$t('conference_sold_out')"
              class="p-button-sm p-button-outlined p-button-secondary w-full justify-content-center"
              disabled
            />
            <Button
              v-else-if="event.canPurchase"
              :label="$t('conference_pay')"
              class="p-button-sm w-full justify-content-center"
              :disabled="!event.link"
              @click="openLanding(event)"
            />
            <p
              v-if="event.canPurchase && !event.link"
              class="mt-2 text-sm text-color-secondary"
            >
              {{ $t("conference_landing_missing") }}
            </p>
          </div>
          <div v-else-if="event.link" class="mt-3" @click.stop>
            <a
              :href="event.link"
              target="_blank"
              rel="noopener noreferrer"
              class="
                p-button p-component p-button-outlined p-button-sm
                w-full
                justify-content-center
              "
            >
              <i class="pi pi-external-link mr-2" />
              <span class="p-button-label">{{ $t("event_open_link") }}</span>
            </a>
          </div>
        </div>
      </div>
    </div>

    <DialogFiles
      :display-files="showFile"
      :files="[{ url: fileUrl, name: fileName }]"
      autoplay
      @closed="showFile = false"
    />
  </div>
</template>

<script>
import ProgressBar from "primevue/progressbar";
import Button from "primevue/button";
import DialogFiles from "../files/DialogFiles.vue";
import ShowUserImage from "../files/ShowUserImage.vue";

export default {
  components: { ProgressBar, Button, DialogFiles, ShowUserImage },
  data() {
    return {
      events: [],
      isLoading: false,
      showFile: false,
      fileUrl: null,
      fileName: null,
    };
  },
  created() {
    this.getEvents();
  },
  methods: {
    isConference(event) {
      return event && event.type === "conference";
    },
    isClickable(event) {
      return !this.isConference(event) && (event.videoUrl || event.link);
    },
    getEvents() {
      this.isLoading = true;
      this.$root
        .apiGet(this.$root.api.profileEvents)
        .then((response) => {
          this.isLoading = false;
          this.events = response.data.data || [];
        })
        .catch((error) => {
          this.isLoading = false;
          this.events = [];
          const msgCode =
            error.response &&
            error.response.data &&
            error.response.data.msg_code;
          if (msgCode) {
            this.$root.toastError(msgCode);
          }
        });
    },
    extractTime(value) {
      if (!value) {
        return null;
      }
      const raw = typeof value === "object" && value.date ? value.date : value;
      const match = String(raw).match(/[T ](\d{2}:\d{2})/);
      return match ? match[1] : null;
    },
    formatSchedule(event) {
      if (!event || !event.startDate) {
        return "";
      }
      const startDate = this.$root.formatDate(event.startDate);
      const endDate =
        event.endDate && this.$root.formatDate(event.endDate) !== startDate
          ? this.$root.formatDate(event.endDate)
          : null;
      const startTime = this.extractTime(event.startTime);
      const endTime = this.extractTime(event.endTime);

      let result = endDate ? `${startDate} - ${endDate}` : startDate;
      if (startTime) {
        result += ` · ${startTime}`;
        if (endTime && endTime !== startTime) {
          result += ` - ${endTime}`;
        }
      }
      return result;
    },
    formatPrice(event) {
      if (!event || event.price == null || event.price === "") {
        return "";
      }
      const amount = Number(event.price);
      if (Number.isNaN(amount)) {
        return "";
      }
      const formatted = amount.toLocaleString("pt-PT", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      const currency =
        event.currency && event.currency !== "eur" ? event.currency : "€";
      return `${formatted} ${currency}`;
    },
    openLanding(event) {
      if (event && event.link) {
        window.open(event.link, "_blank", "noopener");
        return;
      }
      this.$root.toastError(this.$t("conference_landing_missing"));
    },
    openEvent(event) {
      if (this.isConference(event)) {
        return;
      }
      if (event.videoUrl) {
        this.setShowFile(event);
      } else if (event.link) {
        window.open(event.link, "_blank", "noopener");
      }
    },
    setShowFile(event) {
      if (!event.videoUrl) {
        return;
      }
      if (
        event.videoUrl.startsWith("files/") ||
        event.videoUrl.startsWith("https://quadrante.")
      ) {
        this.fileUrl = event.videoUrl;
        this.fileName = event.title;
        this.showFile = true;
      } else {
        window.open(event.videoUrl, "_blank");
      }
    },
  },
};
</script>

<style scoped>
.event-type-tag {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.event-type-tag--conference {
  background: #fff1f2;
  color: #be123c;
  border: 1px solid #fecdd3;
}
</style>
